import { MouseEvent, useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { find } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import Empty from 'src/common/components/Empty';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import { ticketCardVirtualizedContainerSx } from 'src/common/styles/ticketCardStyles';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { useCheckinsByParticipantId } from 'src/hooks/useCheckinsByParticipantId';
import { useMyRegistrationsMaps } from 'src/hooks/useMyRegistrationsMaps';
import { useParticipantEditorModal } from 'src/hooks/useParticipantEditorModal';
import { useParticipantListFilters } from 'src/hooks/useParticipantListFilters';
import usePersistentView from 'src/hooks/usePersistentView';
import { useRegistrationModalState } from 'src/hooks/useRegistrationModalState';
import { LanguageKeys } from 'src/common/constants/languages';
import { confirmRegistrationCreate } from 'src/helpers/confirmRegistrationCreate';
import {
  getCreatedParticipantFromSaveResponse,
  shouldOfferRegistrationAfterParticipantCreate,
} from 'src/helpers/participantCreateRegistrationPrompt';
import {
  getMyParticipantsTourQrAnchorId,
  getMyParticipantsTourRegisterAnchorId,
} from 'src/helpers/myParticipantsTour';
import { useUserParticipantEnrichment } from 'src/hooks/useUserParticipantEnrichment';
import { IFormParticipant } from 'src/interfaces/Participant';
import { IFormRegistration } from 'src/interfaces/Registration';
import { EView } from 'src/interfaces/View';
import VirtualizedParticipantGrid from 'src/pages/Dashboard/Participants/components/VirtualizedParticipantGrid';
import ParticipantFilterForm from 'src/pages/Dashboard/Participants/forms/ParticipantFilterForm';
import ParticipantEditorModal from 'src/pages/Dashboard/Participants/modals/ParticipantEditorModal';
import RegistrationModal from 'src/pages/Dashboard/Participants/modals/RegistrationModal';
import ParticipantsTable from 'src/pages/Dashboard/Participants/tables/ParticipantsTable';
import PendingClaimsSection from 'src/pages/Dashboard/UserParticipants/components/PendingClaimsSection';
import MyParticipantsTourOfferDialog from 'src/pages/Dashboard/UserParticipants/components/MyParticipantsTourOfferDialog';
import { useMyParticipantsTour } from 'src/pages/Dashboard/UserParticipants/hooks/useMyParticipantsTour';
import useCheckinsStore from 'src/stores/checkinsStore';
import useParticipantsStore from 'src/stores/participantsStore';
import useRegistrationsStore from 'src/stores/registrationsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

const CLAIM_PATH = '/dashboard/my/participants/claim';

const UserParticipants = () => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const { activeEditionId, editionsById, competitions, distances } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );
  const eventName =
    activeEditionId && editionsById?.[activeEditionId]
      ? `${t('base.defaultTitle')} ${editionsById[activeEditionId].year}`
      : undefined;

  const {
    participants,
    pendingClaims,
    deleteParticipant: doDeleteParticipant,
    isLoadingParticipants,
    isLoadingPendingClaims,
    saveParticipant: doSaveParticipant,
  } = useParticipantsStore(participantsState => participantsState);

  const {
    registrations,
    fetchMyRegistrations,
    createRegistration,
    updateRegistration,
    deleteRegistration: doDeleteRegistration,
    isLoading: isLoadingRegistrations,
  } = useRegistrationsStore();
  const { checkins, checkinsById } = useCheckinsStore(checkinsState => checkinsState);

  const [view, setView] = usePersistentView();
  const {
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom,
    registeredTo,
    editionFilter,
    order,
    orderByKey,
    setOrder,
    setOrderByKey,
    onChangeFilters,
  } = useParticipantListFilters({ initialEditionId: null });

  const {
    open: participantEditorModalOpen,
    selectedParticipant,
    openEditor: handleOpenParticipantEditorModal,
    closeEditor: handleCloseParticipantEditorModal,
  } = useParticipantEditorModal(participants);

  const {
    open: registrationModalOpen,
    selectedParticipant: registrationParticipant,
    selectedRegistration,
    openForCreate: openRegistrationForCreate,
    openForCreateParticipant: openRegistrationForCreateParticipant,
    openForEdit: openRegistrationForEdit,
    close: handleCloseRegistrationModal,
  } = useRegistrationModalState();

  const handleOpenRegistrationModal = (id?: string, e?: MouseEvent<unknown>) => {
    openRegistrationForCreate(participants, id, e);
  };

  const effectiveEditionId = editionFilter || activeEditionId;
  const isActiveEditionView = !!activeEditionId && effectiveEditionId === activeEditionId;

  const { registrationIdsByParticipantId, enrichmentByParticipantId } = useMyRegistrationsMaps({
    registrations,
    editionId: effectiveEditionId,
  });

  const checkinsByParticipantId = useCheckinsByParticipantId(checkins, effectiveEditionId);

  const sortedParticipants = useUserParticipantEnrichment({
    participants,
    enrichmentByParticipantId,
    registrationIdsByParticipantId,
    effectiveEditionId,
    editionsById,
    checkinsById,
    checkinsByParticipantId,
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom,
    registeredTo,
    orderByKey,
    order,
  });

  const tourRegisterAnchorParticipantId = useMemo(
    () =>
      getMyParticipantsTourRegisterAnchorId(sortedParticipants, {
        isActiveEditionView,
        hasRegisterHandler: isActiveEditionView,
      }),
    [isActiveEditionView, sortedParticipants],
  );

  const tourQrAnchorParticipantId = useMemo(
    () => getMyParticipantsTourQrAnchorId(sortedParticipants),
    [sortedParticipants],
  );

  const tourReady = !isLoadingParticipants && !isLoadingRegistrations && !isLoadingPendingClaims;
  const { offerOpen, startTour, handleOfferDecline, handleOfferStart } = useMyParticipantsTour({
    ready: tourReady,
    showRegisterStep: !!tourRegisterAnchorParticipantId,
    showQrStep: !!tourQrAnchorParticipantId,
  });

  const saveParticipant = async (editedParticipant: IFormParticipant, id?: string) => {
    await doSaveParticipant(editedParticipant, id)
      .then(response => {
        enqueueSnackbar(t('participants.saveSuccessful'), {
          variant: 'success',
        });
        handleCloseParticipantEditorModal();

        if (!shouldOfferRegistrationAfterParticipantCreate(id, activeEditionId, editionsById)) {
          return;
        }

        const created = getCreatedParticipantFromSaveResponse(response);
        if (created?.id) {
          openRegistrationForCreateParticipant(created);
        }
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`race.signUp.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleDeleteRegistration = async (id?: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    const registrationIdToDelete = id ? registrationIdsByParticipantId[id] : undefined;
    if (!registrationIdToDelete) {
      return;
    }
    const contestant = find(sortedParticipants, { id });
    if (contestant?.registration?.status === 'checked_in') {
      enqueueSnackbar(t('registrations.checkedInNotEditable'), { variant: 'error' });
      return;
    }
    const { confirmed } = await confirm({
      title: t('registrations.deleteRegistration'),
      description: t('registrations.deleteRegistrationConfirm'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      return;
    }
    await doDeleteRegistration(registrationIdToDelete)
      .then(() => {
        enqueueSnackbar(t('registrations.deleteRegistrationSuccessful'), { variant: 'success' });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`registrations.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleEditRegistrationWithGuard = (id?: string, e?: MouseEvent<unknown>) => {
    const participant = find(sortedParticipants, { id });
    if (participant?.registration?.status === 'checked_in') {
      enqueueSnackbar(t('registrations.checkedInNotEditable'), { variant: 'error' });
      return;
    }
    openRegistrationForEdit(sortedParticipants, id, e);
  };

  const saveRegistration = async (registration: IFormRegistration) => {
    if (!activeEditionId) {
      enqueueSnackbar(t('registrations.noActiveEdition'), { variant: 'error' });
      return;
    }
    if (registration.id && !isActiveEditionView) {
      enqueueSnackbar(t('registrations.pastEditionNotEditable'), { variant: 'error' });
      return;
    }
    if (registration.id && selectedRegistration?.status === 'checked_in') {
      enqueueSnackbar(t('registrations.checkedInNotEditable'), { variant: 'error' });
      return;
    }

    if (!registration.id) {
      const participant =
        registrationParticipant ||
        find(sortedParticipants, { id: registration.participantId });
      const competition = find(competitions, c => c.id === registration.competitionId);
      const distance = find(distances, d => d.id === registration.distanceId);
      const confirmed = await confirmRegistrationCreate({
        confirm,
        t,
        participantName: participant
          ? `${participant.lastName} ${participant.firstName}`
          : '',
        competitionName: competition?.i18n[currentLang] || '',
        distanceName: distance?.i18n[currentLang] || '',
        price: registration.price,
      });
      if (!confirmed) {
        return;
      }
    }

    const savePromise = registration.id
      ? updateRegistration(registration.id, {
          competitionId: registration.competitionId,
          distanceId: registration.distanceId,
        })
      : createRegistration(
          {
            participantId: registration.participantId!,
            editionId: activeEditionId,
            competitionId: registration.competitionId,
            distanceId: registration.distanceId,
          },
          currentLang,
        );

    await savePromise
      .then(async () => {
        await fetchMyRegistrations();
        enqueueSnackbar(t('registrations.saveSuccessful'), {
          variant: 'success',
        });
        handleCloseRegistrationModal();
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`registrations.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleDeleteParticipant = async (id: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    const { confirmed } = await confirm({
      title: t('participants.deleteConfirm'),
      description: t('participants.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('participants.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteParticipant(id)
      .then(() => {
        enqueueSnackbar(t('participants.deleteSuccessfulMessage'), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`participants.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  return (
    <>
      <DashboardListPageLayout
        title={`${t('participants.pageTitle')} (${participants.length})`}
        isLoading={isLoadingParticipants || isLoadingRegistrations || isLoadingPendingClaims}
        headerActions={
          <>
            <Tooltip title={t('participants.tour.showAgain')}>
              <IconButton
                aria-label={t('participants.tour.showAgain')}
                onClick={startTour}
                size="small"
                color="primary"
              >
                <HelpOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Button
              {...dashboardPrimaryActionButtonProps()}
              onClick={() => handleOpenParticipantEditorModal()}
              startIcon={<AddIcon />}
              data-tour="my-participants-add"
            >
              {t('participants.new')}
            </Button>
            <Button
              {...dashboardPrimaryActionButtonProps()}
              onClick={() => navigate(CLAIM_PATH)}
              startIcon={<PersonSearchIcon />}
              data-tour="my-participants-claim"
            >
              {t('empty.participants.claim')}
            </Button>
          </>
        }
        toolbar={
          <ParticipantFilterForm
            checkedInFilter={checkedInFilter}
            competitionFilter={competitionFilter}
            distanceFilter={distanceFilter}
            editionFilter={editionFilter}
            registeredFrom={registeredFrom}
            registeredTo={registeredTo}
            onChange={onChangeFilters}
            onSetView={setView}
            query={query}
            view={view}
            order={order}
            setOrder={setOrder}
            orderByKey={orderByKey}
            setOrderByKey={setOrderByKey}
          />
        }
      >
        <Box
          data-tour="my-participants-list"
          sx={{
            flex: '1 1 0',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        {!isLoadingParticipants &&
          !isLoadingRegistrations &&
          sortedParticipants.length === 0 &&
          participants.length > 0 &&
          (query ||
            competitionFilter.length > 0 ||
            distanceFilter.length > 0 ||
            checkedInFilter.length > 0 ||
            registeredFrom ||
            registeredTo) && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                ...frostedPanelSx(0.5),
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('participants.hiddenByFilters', { count: participants.length })}
              </Typography>
            </Paper>
          )}
        {!isLoadingParticipants &&
          !isLoadingRegistrations &&
          participants.length === 0 &&
          !pendingClaims.length && (
            <Empty
              icon={<PeopleOutlinedIcon />}
              title={t('empty.participants.title')}
              description={t('empty.participants.description')}
              primaryAction={{
                label: t('empty.addNew'),
                onClick: () => handleOpenParticipantEditorModal(),
              }}
              secondaryAction={{
                label: t('empty.participants.claim'),
                onClick: () => navigate(CLAIM_PATH),
              }}
            />
          )}
        {!!sortedParticipants.length && (
          <Box sx={ticketCardVirtualizedContainerSx}>
            {view === EView.grid && (
              <VirtualizedParticipantGrid
                participants={sortedParticipants}
                register={isActiveEditionView ? handleOpenRegistrationModal : undefined}
                editRegistration={isActiveEditionView ? handleEditRegistrationWithGuard : undefined}
                deleteRegistration={isActiveEditionView ? handleDeleteRegistration : undefined}
                deleteParticipant={handleDeleteParticipant}
                editParticipant={handleOpenParticipantEditorModal}
                registrationIds={registrationIdsByParticipantId}
                tourRegisterAnchorParticipantId={tourRegisterAnchorParticipantId}
                tourQrAnchorParticipantId={tourQrAnchorParticipantId}
              />
            )}
            {view === EView.table && (
              <ParticipantsTable
                layout="my"
                participants={sortedParticipants}
                register={isActiveEditionView ? handleOpenRegistrationModal : undefined}
                editRegistration={isActiveEditionView ? handleEditRegistrationWithGuard : undefined}
                deleteRegistration={isActiveEditionView ? handleDeleteRegistration : undefined}
                deleteParticipant={handleDeleteParticipant}
                editParticipant={handleOpenParticipantEditorModal}
                registrationIds={registrationIdsByParticipantId}
                tourRegisterAnchorParticipantId={tourRegisterAnchorParticipantId}
                tourQrAnchorParticipantId={tourQrAnchorParticipantId}
              />
            )}
          </Box>
        )}
        {!isLoadingPendingClaims && !!pendingClaims.length && (
          <PendingClaimsSection claims={pendingClaims} />
        )}
        </Box>
      </DashboardListPageLayout>

      {participantEditorModalOpen && (
        <ParticipantEditorModal
          id="participant-editor"
          open={participantEditorModalOpen}
          handleClose={handleCloseParticipantEditorModal}
          handleSave={saveParticipant}
          isExternalSignup
          selectedParticipant={selectedParticipant}
        />
      )}

      {registrationModalOpen && !!registrationParticipant && (effectiveEditionId || activeEditionId) && (
        <RegistrationModal
          id="registration"
          eventName={eventName}
          isLoading={isLoadingRegistrations}
          open={registrationModalOpen}
          handleClose={handleCloseRegistrationModal}
          handleSave={saveRegistration}
          selectedParticipant={registrationParticipant}
          existingRegistration={selectedRegistration}
        />
      )}

      <MyParticipantsTourOfferDialog
        open={offerOpen}
        onStart={handleOfferStart}
        onDecline={handleOfferDecline}
      />
    </>
  );
};

export default UserParticipants;
